"use client";

import React from "react";
import { CircleQuestionMark, Battery, Cpu, Truck, ShieldCheck, Wifi } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tips = [{
  id: "item-1",
  question: "What are the advantages of G657A2 fiber in high-density data centers?",
  answer: "G657A2 optical fiber offers superior bending resistance, allowing for significantly smaller bend radii without increasing attenuation. This is critical for high-density AI data center infrastructure where cable routing space is limited and signal integrity is paramount for high-bandwidth connectivity.",
  icon: <Battery className="size-5 text-primary" />
}, {
  id: "item-2",
  question: "How does Nexel ensure signal stability in FPV video transmission?",
  answer: "Our FPV systems utilize advanced diversity receivers and high-gain antennas designed for minimal interference. By optimizing signal-to-noise ratios and using low-latency video transmission protocols, we ensure a reliable link even in challenging electromagnetic environments.",
  icon: <Wifi className="size-5 text-primary" />
}, {
  id: "item-3",
  question: "What processing power do your latest flight controllers offer?",
  answer: "Nexel flight controllers are powered by high-performance CPUs capable of handling complex PID loops and AI-driven stabilization algorithms at high refresh rates. This ensures precise control and responsiveness for professional drone racing and industrial applications.",
  icon: <Cpu className="size-5 text-primary" />
}, {
  id: "item-4",
  question: "How do you handle global distribution for telecom operators?",
  answer: "We maintain a robust global logistics network capable of delivering large-scale optical fiber orders and drone components to telecom operators and distributors worldwide. Every shipment is tracked in real-time to ensure timely arrival and project continuity.",
  icon: <Truck className="size-5 text-primary" />
}, {
  id: "item-5",
  question: "What quality assurance protocols are applied to Nexel hardware?",
  answer: "All Nexel products undergo rigorous testing, including environmental stress screening and performance validation against international standards. We hold ISO9001 certifications and ensure every optical fiber and drone component meets strict precision engineering benchmarks.",
  icon: <ShieldCheck className="size-5 text-primary" />
}];
export default function QuickTips() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="35621" id="knowledge-base" ref={ref} className={`py-20 bg-background border-y border-border transition-opacity duration-700 dark ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-4">
            <CircleQuestionMark className="size-6 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
            Technical Knowledge Base
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Engineering insights and expert advice on optical communication, AI infrastructure, 
            and high-performance FPV drone systems.
          </p>
        </div>

        <div className="bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            {tips.map((tip, i) => <AccordionItem key={tip.id} value={tip.id} className="px-6 border-border/50 last:border-0" data-index={i}>
                <AccordionTrigger className="hover:no-underline hover:text-primary py-6 text-base md:text-lg font-medium transition-colors text-foreground">
                  <div className="flex items-center gap-4 text-left">
                    <span className="hidden sm:flex shrink-0 items-center justify-center size-8 rounded-full bg-secondary/50 text-secondary-foreground">
                      {tip.icon}
                    </span>
                    {tip.question}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6 pl-0 sm:pl-12">
                  {tip.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-6">
            Need specific technical documentation or integration support? Our engineers are ready to assist.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="mailto:support@galabit.com">Contact Support</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-primary/20 hover:bg-primary/5 text-foreground">
              <Link to="/SupportPage">Technical Resources</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}